import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleCategorySelect = (url: string) => {
    setDropdownOpen(false);
    setMenuOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const textClass = scrolled
    ? "text-foreground-950"
    : "text-background-50";
  const textMutedClass = scrolled
    ? "text-foreground-600 hover:text-foreground-950"
    : "text-background-50/80 hover:text-background-50";
  const borderClass = scrolled
    ? "border-foreground-200/60"
    : "border-background-50/30";
  const dropdownBgClass = scrolled
    ? "bg-background-50/95 backdrop-blur-md"
    : "bg-foreground-900/80 backdrop-blur-md";
  const mobileBgClass = scrolled
    ? "bg-background-50/98 backdrop-blur-md"
    : "bg-foreground-900/95 backdrop-blur-md";
  const mobileTextClass = scrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-background-50/85 hover:text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <div className="flex items-center gap-4 md:gap-6">
          <a
            href="/"
            className={`text-lg md:text-xl font-heading font-bold transition-opacity cursor-pointer whitespace-nowrap ${textClass} ${
              scrolled ? "hover:opacity-80" : "hover:opacity-90"
            }`}
          >
            CSSBuy Spreadsheet
          </a>
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textMutedClass}`}
            >
              <i className="ri-home-line mr-1"></i>Início
            </button>
            <a href="#categories" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textMutedClass}`}>
              {t("sectionCategories")}
            </a>
            <a href="#comparison" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textMutedClass}`}>
              Comparação
            </a>
            <a href="#faq" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textMutedClass}`}>
              FAQ
            </a>
            <a href="#articles" className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textMutedClass}`}>
              <i className="ri-book-open-line mr-1"></i>Guias
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:flex items-center gap-2" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm transition-all whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? "border-foreground-200/60 bg-background-50/80 text-foreground-700 hover:text-foreground-950 hover:border-foreground-300"
                    : "border-background-50/30 bg-background-50/15 text-background-50/90 hover:bg-background-50/25"
                }`}
              >
                <span>{t("categorySelect")}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl overflow-hidden shadow-lg z-50 max-h-80 overflow-y-auto">
                  <div className="p-2">
                    <p className="px-3 py-1.5 text-xs text-foreground-400 font-medium uppercase tracking-wider">Todas as Categorias</p>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-3"
                      >
                        <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 text-xs flex-shrink-0">
                          <i className="ri-arrow-right-up-line"></i>
                        </span>
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium">{cat.namePt}</span>
                          <span className="text-xs text-foreground-400">{cat.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-background-50/90 text-foreground-900 hover:bg-background-50"
              }`}
            >
              Guia de Compras <i className="ri-external-link-line ml-1 text-xs"></i>
            </a>
          </div>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              scrolled ? "hover:bg-background-100" : "hover:bg-background-50/15"
            }`}
          >
            <i className={`text-xl ${menuOpen ? (scrolled ? "text-foreground-800" : "text-background-50") : (scrolled ? "text-foreground-800" : "text-background-50")} ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className={`md:hidden px-4 pb-5 pt-2 ${mobileBgClass}`}>
          <div className="flex flex-col gap-1">
            <button
              onClick={() => { navigate("/"); setMenuOpen(false); }}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}
            >
              <span className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-home-line text-primary-600 text-sm"></i>
              </span>
              Início
            </button>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMenuOpen(false)}
              className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}
            >
              <span className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-bag-line text-accent-600 text-sm"></i>
              </span>
              Guia de Compras
            </a>

            <div className="border-t border-white/10 my-2"></div>

            <p className="px-3 py-1.5 text-xs text-background-50/40 font-semibold uppercase tracking-wider">Navegação</p>

            <a href="#categories" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}>
              <span className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-grid-line text-secondary-600 text-sm"></i>
              </span>
              {t("sectionCategories")}
            </a>
            <a href="#comparison" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}>
              <span className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-bar-chart-line text-secondary-600 text-sm"></i>
              </span>
              Comparação de Produtos
            </a>
            <a href="#faq" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}>
              <span className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-question-line text-secondary-600 text-sm"></i>
              </span>
              FAQ
            </a>
            <a href="#articles" onClick={() => setMenuOpen(false)} className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}>
              <span className="w-7 h-7 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-book-open-line text-secondary-600 text-sm"></i>
              </span>
              Guias e Artigos
            </a>

            <div className="border-t border-white/10 my-2"></div>
            <p className="px-3 py-1.5 text-xs text-background-50/40 font-semibold uppercase tracking-wider">{t("allCategories")}</p>

            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer ${mobileTextClass}`}
                >
                  <i className="ri-arrow-right-up-line text-xs flex-shrink-0"></i>
                  <span className="truncate">{cat.namePt}</span>
                </a>
              ))}
            </div>

            <div className="border-t border-white/10 my-2"></div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMenuOpen(false)}
              className="mt-1 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t("btnBuyNow")} <i className="ri-arrow-right-up-line ml-1"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}